// =============================================================================
// Fase 5.6 — Step Garanzie di Interesse (multi-select, RCA sempre inclusa)
// Mostrato solo per percorsi nuova/rinnovo/fermo (NON per contatto).
// Posizione pipeline: PRIMA del Step6 Contatto, DOPO doc/veicolo/Bersani.
// =============================================================================
import React from "react";
import { Shield, Flame, Car, Sparkles, HeartPulse, Wrench, Scale, CheckCircle2 } from "lucide-react";

// ID canonici delle garanzie (usati anche lato backend)
export const GARANZIE = [
  {
    id: "rca",
    label: "RC Auto",
    required: true, // sempre inclusa
    icon: Shield,
    desc: "Copertura obbligatoria per legge. Danni causati a terzi.",
  },
  {
    id: "furto_incendio",
    label: "Furto & Incendio",
    icon: Flame,
    desc: "Risarcimento in caso di furto totale/parziale o incendio del veicolo.",
  },
  {
    id: "kasko",
    label: "Kasko / CVT",
    icon: Car,
    desc: "Danni al veicolo anche in caso di sinistro con tua colpa o senza controparte.",
  },
  {
    id: "cristalli",
    label: "Cristalli",
    icon: Sparkles,
    desc: "Riparazione o sostituzione parabrezza e vetri.",
  },
  {
    id: "infortuni_conducente",
    label: "Infortuni conducente",
    icon: HeartPulse,
    desc: "Indennizzo per lesioni al conducente in caso di sinistro.",
  },
  {
    id: "assistenza_stradale",
    label: "Assistenza stradale",
    icon: Wrench,
    desc: "Traino, soccorso e veicolo sostitutivo 24/7.",
  },
  {
    id: "tutela_legale",
    label: "Tutela legale",
    icon: Scale,
    desc: "Assistenza legale per controversie stradali.",
  },
];

export function StepGaranzie({ state, setField }) {
  const selected = new Set(state.garanzie_interesse || ["rca"]);

  const toggle = (id) => {
    if (id === "rca") return; // non toggleable
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // RCA sempre inclusa
    next.add("rca");
    // Preserva l'ordine canonico
    const ordered = GARANZIE.map((g) => g.id).filter((x) => next.has(x));
    setField("garanzie_interesse", ordered);
  };

  return (
    <div className="space-y-4" data-testid="rca-step-garanzie">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
          Quali garanzie ti interessano?
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1">
          Seleziona le coperture che vorresti valutare nel preventivo.
          RC Auto è sempre inclusa perché obbligatoria per legge.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {GARANZIE.map((g) => {
          const Icon = g.icon;
          const on = selected.has(g.id);
          const isRequired = !!g.required;
          return (
            <button
              key={g.id}
              type="button"
              disabled={isRequired}
              onClick={() => toggle(g.id)}
              data-testid={`rca-garanzia-${g.id}`}
              data-checked={on ? "true" : "false"}
              className={`text-left rounded-2xl border-2 p-4 transition-all relative ${
                on
                  ? "border-[var(--brand-accent,#163321)] bg-[#163321]/5 ring-2 ring-[var(--brand-accent,#163321)]/20"
                  : "border-[#E5E2DB] bg-white hover:border-[#4A5E51]"
              } ${isRequired ? "cursor-not-allowed opacity-95" : "cursor-pointer"}`}
            >
              <div className="flex items-start gap-3">
                <Icon
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    on ? "text-[var(--brand-accent,#163321)]" : "text-[#4A5E51]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#0F1F14]">{g.label}</span>
                    {isRequired && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--brand-accent,#163321)] text-white">
                        obbligatoria
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[#4A5E51] mt-0.5 leading-snug">{g.desc}</p>
                </div>
                {on && (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-[#7A8E83]">
        Puoi cambiare le garanzie parlando con il consulente in fase di preventivo.
      </p>
    </div>
  );
}
